import React, { createContext, useContext, useEffect, useState } from 'react';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Charger le panier depuis le localStorage au chargement
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCartItems(JSON.parse(savedCart));
    }
  }, []);

  // Sauvegarder le panier dans le localStorage à chaque modification
  useEffect(() => {
    if (cartItems.length > 0) {
      localStorage.setItem('cart', JSON.stringify(cartItems));
    } else {
      localStorage.removeItem('cart');
    }
  }, [cartItems]);

  const addToCart = (product, quantity = 1) => {
    setCartItems(prevItems => {
      const isOnSale = (product.sale && product.sale.isActive) || 
                      (product.salePrice !== undefined && product.salePrice !== null) ||
                      (product.promotion && product.promotion.type === 'sale');
                      
      const salePrice = product.salePrice !== undefined && product.salePrice !== null
        ? parseFloat(product.salePrice)
        : (product.sale?.discountedPrice ? parseFloat(product.sale.discountedPrice) : null);
      
      const price = parseFloat(product.price) || 0;
      const originalPrice = parseFloat(product.originalPrice) || price;
      const finalPrice = isOnSale && salePrice !== null ? salePrice : price;

      const existingItemIndex = prevItems.findIndex(item => item.id === product.id);
      
      if (existingItemIndex >= 0) {
        return prevItems.map((item, index) => 
          index === existingItemIndex
            ? {
                ...item,
                quantity: item.quantity + quantity,
                price: price,
                salePrice: salePrice,
                originalPrice: originalPrice,
                sale: product.sale || item.sale,
                promotion: product.promotion || item.promotion
              }
            : item
        );
      }
      
      const newItem = {
        ...product,
        quantity,
        price: price,
        salePrice: isOnSale ? salePrice : null,
        originalPrice: originalPrice,
        promotion: product.promotion || (isOnSale ? {
          type: 'sale',
          discount: product.sale?.discountPercentage || 
                   Math.round(((originalPrice - finalPrice) / originalPrice) * 100),
          saleId: product.sale?.id || 'sale_' + product.id
        } : null)
      };
      
      return [...prevItems, newItem];
    });
  };

  const removeFromCart = (productId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity < 1) {
      removeFromCart(productId);
      return;
    }
    
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    let total = 0;
    
    cartItems.forEach(item => {
      const effectivePrice = (item.salePrice !== null && item.salePrice !== undefined)
        ? parseFloat(item.salePrice)
        : parseFloat(item.price) || 0;
      
      total += effectivePrice * (item.quantity || 1);
    });
    
    return total;
  };

  // 👇 Fonctions globales d'ouverture/fermeture du panier
  const openCart = () => {
    setIsCartOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const toggleCart = () => {
    setIsCartOpen(prev => {
      const next = !prev;
      document.body.style.overflow = next ? 'hidden' : 'unset';
      return next;
    });
  };

  const closeCart = () => {
    setIsCartOpen(false);
    document.body.style.overflow = 'unset';
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        isCartOpen,
        setIsCartOpen, // si tu en as besoin ailleurs
        openCart,
        toggleCart,
        closeCart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotalItems,
        getTotalPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Version sécurisée avec valeur par défaut
export const useCart = () => {
  const context = useContext(CartContext);
  
  // Si le contexte n'existe pas (pas de CartProvider), retourner un objet avec des valeurs par défaut
  if (!context) {
    console.warn('useCart must be used within a CartProvider');
    return {
      cartItems: [],
      isCartOpen: false,
      setIsCartOpen: () => {},
      openCart: () => {},
      toggleCart: () => {},
      closeCart: () => {},
      addToCart: () => {},
      removeFromCart: () => {},
      updateQuantity: () => {},
      clearCart: () => {},
      getTotalItems: () => 0,
      getTotalPrice: () => 0
    };
  }
  
  return context;
};
